import { useContext, useEffect, useState } from 'react';
import {
    computePredictedRemainingVolume,
    getLiquiditySummaryData,
} from 'widgets/liquiditySummary/liquiditySummaryData';
import { LiquiditySummaryDataState } from 'widgets/liquiditySummary/store/data/LiquiditySummaryDataState';
import { liquidityStreamedDataSelectors } from 'widgets/liquiditySummary/store/data/streamSlice';
import { usePortfolioSelectedSymbolQuantity } from 'portfolio/store/hooks';
import isNil from 'lodash/isNil';
import { safeCalculatePercent } from 'utils/percent';
import AppContext from 'app/AppContext';
import { NumberFormat } from '@instinet-analytics/core-lib';
import { usePredictedVolumeCapsuleConfigSlice } from 'widgets/liquiditySummary/predictedVolumeCapsule/state/config/configslice';

interface PredictedVolumeCapsuleDataSegment {
    value: number;
    className: string;
    tooltip?: string;
}
export interface PredictedVolumeCapsuleData {
    predictedRemainingVolume: number;
    predCloseVol: number;
    orderLeaves: number;
    orderLeavesPercent: number;
    labels: {
        predictedRemainingVolume: string;
        predCloseVol: string;
        orderLeaves: string;
        orderLeavesPercent: string;
        todayVsHist: string;
    };
    segments: PredictedVolumeCapsuleDataSegment[];
}

export function usePredictedCloseVolumeCapsuleData():
    | PredictedVolumeCapsuleData
    | undefined {
    const { useWidgetStreamData } = liquidityStreamedDataSelectors;
    const streamData = useWidgetStreamData();
    const symbolQuantity = usePortfolioSelectedSymbolQuantity();
    const { numberFormat } = useContext(AppContext);
    const {
        config: { includePredContinuousTrading },
    } = usePredictedVolumeCapsuleConfigSlice();

    const [chartData, setChartData] = useState<
        PredictedVolumeCapsuleData | undefined
    >(undefined);

    useEffect(() => {
        const data = buildData(
            streamData,
            symbolQuantity?.symbol,
            symbolQuantity?.netPosition.qty,
            symbolQuantity?.netPosition.qtyRemaining,
            numberFormat,
            includePredContinuousTrading
        );
        setChartData(data);
    }, [
        streamData,
        symbolQuantity,
        numberFormat,
        includePredContinuousTrading,
    ]);

    return chartData;
}

export function buildData(
    streamData: LiquiditySummaryDataState | undefined,
    symbol: string | undefined,
    qty: number | undefined,
    qtyRemaining: number | undefined,
    numberFormat: NumberFormat,
    includePredContinuousTrading: boolean
): PredictedVolumeCapsuleData | undefined {
    const liquiditySummaryData = getLiquiditySummaryData(
        streamData,
        1,
        1,
        1,
        'B'
    );

    if (!liquiditySummaryData || !symbol) {
        return undefined;
    }

    const {
        predCloseVol,
        stats: {
            predictedRemaining: { totalVsHist: todayVsHist },
        },
    } = liquiditySummaryData;
    const predictedRemainingVolume =
        computePredictedRemainingVolume(liquiditySummaryData);

    if (
        (isNil(predCloseVol) && isNil(predictedRemainingVolume)) ||
        isNil(qtyRemaining)
    ) {
        return undefined;
    }

    const totalPredictedRemaining = includePredContinuousTrading
        ? predictedRemainingVolume ?? 0
        : predCloseVol ?? 0;

    const orderLeavesPercent = safeCalculatePercent(
        qtyRemaining,
        totalPredictedRemaining
    );
    const orderLeavesPercentLabel = numberFormat.n1(orderLeavesPercent);
    const predictedRemainingVolumeLabel = numberFormat.n0(
        predictedRemainingVolume
    );
    const predCloseVolLabel = numberFormat.n0(predCloseVol);

    const todayVsHistLabel = numberFormat.n0(todayVsHist);

    const qtyRemainingLabel = numberFormat.n0(qtyRemaining);

    const segments = computeSegments({
        predictedRemainingVolume: predictedRemainingVolume ?? 0,
        predCloseVol: predCloseVol ?? 0,
        qtyRemainingLabel,
        predCloseVolLabel: predCloseVolLabel,
        totalPredictedRemaining,
        qtyRemaining,
        orderLeavesPercentLabel,
        includePredContinuousTrading,
        numberFormat,
    });

    return {
        predictedRemainingVolume: predictedRemainingVolume ?? 0,
        predCloseVol: predCloseVol ?? 0,
        orderLeaves: qtyRemaining,
        orderLeavesPercent,
        labels: {
            predictedRemainingVolume: predictedRemainingVolumeLabel,
            predCloseVol: predCloseVolLabel,
            orderLeaves: qtyRemainingLabel,
            orderLeavesPercent: orderLeavesPercentLabel,
            todayVsHist: todayVsHistLabel,
        },
        segments,
    };
}

function computeSegments(options: {
    qtyRemainingLabel: string;
    orderLeavesPercentLabel: string;
    predCloseVolLabel: string;
    includePredContinuousTrading: boolean;
    qtyRemaining: number;
    predictedRemainingVolume: number;
    predCloseVol: number;
    numberFormat: NumberFormat;
    totalPredictedRemaining: number;
}) {
    const {
        qtyRemainingLabel,
        orderLeavesPercentLabel,
        predCloseVolLabel,
        includePredContinuousTrading,
        qtyRemaining,
        predCloseVol,
        predictedRemainingVolume,
        numberFormat,
        totalPredictedRemaining,
    } = options;

    const remTooltip = `${qtyRemainingLabel} (${orderLeavesPercentLabel}% of ${
        includePredContinuousTrading
            ? 'predicted remaining volume'
            : 'predicted market closing auction'
    })`;

    const remSegment: PredictedVolumeCapsuleDataSegment = {
        className: 'order-leaves',
        tooltip: remTooltip,
        value:
            qtyRemaining > 0
                ? Math.max(qtyRemaining, totalPredictedRemaining * 0.02)
                : 0,
    };

    if (qtyRemaining >= totalPredictedRemaining) {
        if (totalPredictedRemaining === 0) {
            remSegment.tooltip = remSegment.tooltip?.replace('0.0% of', 'no');
        }

        // Total predicted remaining is hidden by leaves
        return [remSegment];
    }

    const auctionSegment = (value: number) => ({
        className: 'auction',
        value,
        tooltip: `${predCloseVolLabel} predicted closing auction`,
    });

    if (!includePredContinuousTrading) {
        // Auction only
        if (qtyRemaining === 0) {
            return [auctionSegment(predCloseVol)];
        }

        if (qtyRemaining > predCloseVol) {
            return [remSegment];
        }

        return [
            remSegment,
            auctionSegment(totalPredictedRemaining - remSegment.value),
        ];
    }

    // Including continuous trading and auction...
    const predContinuousTradingVolume = predictedRemainingVolume - predCloseVol;

    const continuousTradingSegment = (value: number) => ({
        className: 'pred-continuous-trading',
        value,
        tooltip: `${numberFormat.n0(
            predContinuousTradingVolume
        )} predicted continuous remaining volume`,
    });

    const segments: PredictedVolumeCapsuleDataSegment[] = [];

    if (qtyRemaining > 0) {
        segments.push(remSegment);
    }

    if (qtyRemaining >= totalPredictedRemaining) {
        return segments;
    }

    if (qtyRemaining < predContinuousTradingVolume) {
        segments.push(
            continuousTradingSegment(
                totalPredictedRemaining - qtyRemaining - predCloseVol
            )
        );
        if (predCloseVol > 0) {
            segments.push(auctionSegment(predCloseVol));
        }
    } else {
        segments.push(auctionSegment(totalPredictedRemaining - qtyRemaining));
    }

    return segments;
}
